import { skipToken } from '@reduxjs/toolkit/query'
import { useCallback, useMemo, useState } from 'react'
import { ThemeProvider } from 'styled-components'
import { getImageFromTimeSlot, type ReservationHoldErrorResponse } from '@sevenrooms/domain'
import { ReservationWidget } from '@sevenrooms/domain/constants'
import { type Field, useWatchMany } from '@sevenrooms/form'
import { useLocales } from '@sevenrooms/locales'
import { DateOnly, TimeOnly } from '@sevenrooms/timepiece'
import { Form } from '@sevenrooms/ui-kit/form'
import { Flex, Window } from '@sevenrooms/ui-kit/layout'
import { Text } from '@sevenrooms/ui-kit/typography'
import { ReservationHoldModal } from '../../../components'
import { AvailabilityResults } from '../../../components/AvailabilityResults'
import {
  useCachedCreateReservationHold,
  useReservationsRoute,
  useVenue,
  useWidgetSettings,
  useReservationNavigation,
  useGroupOrCrossSellWidget,
  type AvailabilityTimeWithUpSellCost,
  useOtherVenueTheme,
  useAvailabilityDates,
} from '../../../hooks'
import { reservationWidgetMessages } from '../../../reservationWidgetMessages'
import { useGetGuestFacingUpgradeQuery, useReservationFormState, useModals } from '../../../store'
import {
  selectTimeslot,
  confirmTimeslotWithPaidUpgrades,
  finishSearchPage,
  mapAvailabilityUpgrades,
  updateTimeSearchParams,
  updatePartySizeSearchParams,
  updateDateSearchParams,
  trackTealiumChangeVenue,
  trackTealiumChangeQuantity,
  trackTealiumChangeDate,
} from '../../../utils'
import { ConfirmOtherVenueNavigationModal } from '../ConfirmOtherVenueNavigationModal'
import { ExperienceDetailsModal } from '../ExperienceDetailsModal'
import { ReservationAvailabilityModal } from '../ReservationAvailabilityModal'
import { ReservationSearchComponent } from '../ReservationSearchComponent'
import { StyledSearchInputGridContainer } from '../ReservationSearchInputGroup'
import { ReservationsGroupWidget } from '../ReservationsGroupWidget'
import { useSearchForm } from '../useSearchForm'
import { getReservationHoldErrorMessage, shouldShowAvailabilityModal } from '../utils'
import type { ExperienceWithAvailability } from '../../../hooks/useAvailability'
import type { OtherVenueInfo } from '@/domain'

interface ReservationsTabProps {
  showPrivateEvents: boolean
  isModifyRoute?: boolean
}

export function ReservationsTab({ showPrivateEvents, isModifyRoute }: ReservationsTabProps) {
  const { activeModal, showModal, hideModal, showErrorModal } = useModals()
  const { navigateNext: navigateNextStep, disableStep, enableStep } = useReservationNavigation()
  const {
    formState: { ...searchFormData },
    updateFormState,
  } = useReservationFormState()
  const venue = useVenue()
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<AvailabilityTimeWithUpSellCost>()
  const [selectedOtherVenue, setSelectedOtherVenue] = useState<OtherVenueInfo | undefined>()
  const [selectedExperience, setSelectedExperience] = useState<ExperienceWithAvailability>()
  const widgetSettings = useWidgetSettings()
  const { formatMessage } = useLocales()
  const { updateQuery, channel } = useReservationsRoute()
  const [createReservationHold] = useCachedCreateReservationHold()
  const { urlKey, name: venueName, currencyCode } = venue
  const showDurationPicker = searchFormData.durationPickerEnabled

  const theme = useOtherVenueTheme(selectedOtherVenue)

  const form = useSearchForm({
    partySize: searchFormData.partySize,
    startDate: searchFormData.startDate,
    startTime: searchFormData.startTime,
    duration: searchFormData.duration,
    haloTimeIntervalMinutes: searchFormData.haloTimeIntervalMinutes,
  })

  const { field, handleSubmit, setValue } = form
  const [partySize, startDate, startTime, duration, haloTimeIntervalMinutes] = useWatchMany(field, [
    'partySize',
    'startDate',
    'startTime',
    'duration',
    'haloTimeIntervalMinutes',
  ])

  const { isGroupWidget, venuesInfo, isFetchingVenuesInfo } = useGroupOrCrossSellWidget()

  const { isFetching: isDatesFetching, isDayBlocked } = useAvailabilityDates()

  const { data: upgradesData } = useGetGuestFacingUpgradeQuery(isGroupWidget ? skipToken : venue.id)

  const navigateNext = useCallback(
    async (timeslot: AvailabilityTimeWithUpSellCost, venueUrlKey?: string, query?: { [key: string]: string }) => {
      const { reservationHoldEnabled } = widgetSettings
      const { accessPersistentId, shiftPersistentId, time, timeIso } = timeslot
      const date = DateOnly.from(timeIso).toIso()
      finishSearchPage(urlKey)
      if (!venueUrlKey && reservationHoldEnabled) {
        try {
          showModal('reservationHold')
          await createReservationHold({
            accessPersistentId,
            channel: channel || ReservationWidget.SevenRoomsWidgetChannel,
            date,
            partySize,
            pickedDuration: duration,
            shiftPersistentId,
            time,
            venueId: venue.id,
            trackingSlug: searchFormData.trackingSlug,
            clientId: searchFormData.clientId,
          }).unwrap()
          navigateNextStep()
        } catch (err) {
          const errorMessage = getReservationHoldErrorMessage(err as ReservationHoldErrorResponse, formatMessage)
          showErrorModal(errorMessage, undefined, true)
        }
        hideModal()
      } else {
        navigateNextStep(venueUrlKey, query)
      }
    },
    [
      widgetSettings,
      urlKey,
      hideModal,
      showModal,
      createReservationHold,
      channel,
      partySize,
      duration,
      venue.id,
      searchFormData.trackingSlug,
      searchFormData.clientId,
      navigateNextStep,
      formatMessage,
      showErrorModal,
    ]
  )

  const handleFilterTimeChange = ({ startTime, haloTimeIntervalMinutes }: { startTime: string; haloTimeIntervalMinutes: number }) => {
    setValue('startTime', startTime)
    setValue('haloTimeIntervalMinutes', haloTimeIntervalMinutes)
    updateFormState({ startTime, haloTimeIntervalMinutes })
    updateQuery({ time: startTime?.toString(), halo: haloTimeIntervalMinutes?.toString() })
    updateTimeSearchParams(venue.urlKey, startTime, haloTimeIntervalMinutes)
    hideModal()
  }

  const updateSelectedTimeslotInfo = useCallback(
    (timeSlot: AvailabilityTimeWithUpSellCost | undefined, venue: OtherVenueInfo | undefined) => {
      setSelectedTimeSlot(timeSlot)
      setSelectedOtherVenue(venue)
    },
    []
  )

  const onSelectedTimeslotModalClose = useCallback(() => {
    hideModal()
    updateSelectedTimeslotInfo(undefined, undefined)
  }, [hideModal, updateSelectedTimeslotInfo])

  const onAvailabilitySelect = useCallback(
    async (
      timeSlot: AvailabilityTimeWithUpSellCost,
      date?: string,
      selectedVenue?: OtherVenueInfo,
      experience?: ExperienceWithAvailability
    ) => {
      let partySize
      let startDate
      let duration
      await handleSubmit(data => {
        partySize = data.partySize
        duration = data.duration
        startDate = date ? DateOnly.from(date).toIso() : DateOnly.fromDate(data.startDate).toIso()

        // update state when other availability is selected.
        updateFormState({
          partySize,
          startDate,
          duration,
          selectedTimeSlot: timeSlot,
          selectedExperience: experience,
        })
        // eslint-disable-next-line no-warning-comments
        selectTimeslot(selectedVenue?.urlKey ?? venue.urlKey, DateOnly.fromDate(data.startDate).toIso(), timeSlot.time, data.partySize)
        if (timeSlot.upsellCategories && timeSlot.upsellCategories.length > 0) {
          enableStep('upgrades')
        } else {
          disableStep('upgrades')
        }
      })()
      if (!experience && shouldShowAvailabilityModal(timeSlot)) {
        updateSelectedTimeslotInfo(timeSlot, selectedVenue)
        showModal('reservationAvailability')
      } else if (experience) {
        setSelectedTimeSlot(timeSlot)
        setSelectedExperience(experience)
        setSelectedOtherVenue(selectedVenue)
        showModal('moreExperienceDetails')
      } else if (isGroupWidget) {
        if (timeSlot.accessPersistentId) {
          const timeIso = TimeOnly.from(timeSlot.timeIso).toHoursMinutesIso()
          navigateNext(timeSlot, selectedVenue?.urlKey, {
            date: timeSlot.timeIso,
            timeslot_id: timeSlot.accessPersistentId,
            timeslot_time: timeIso,
            primary_venue_id: venue.id,
          })
        }
      } else if (selectedVenue && selectedVenue?.urlKey !== venue.urlKey) {
        updateSelectedTimeslotInfo(timeSlot, selectedVenue)
        showModal('confirmOtherVenueNavigation')
      } else {
        // update query params when other availability is selected.
        updateQuery({
          date: startDate,
          partySize,
          duration,
          clientId: searchFormData.clientId,
        })
        navigateNext(timeSlot, selectedVenue?.urlKey)
      }
    },
    [
      handleSubmit,
      venue.urlKey,
      venue.id,
      isGroupWidget,
      updateFormState,
      enableStep,
      disableStep,
      updateSelectedTimeslotInfo,
      showModal,
      navigateNext,
      updateQuery,
      searchFormData.clientId,
    ]
  )
  const onConfirmAvailabilitySelect = (timeSlot: AvailabilityTimeWithUpSellCost) => {
    const startDate = DateOnly.from(timeSlot.timeIso).toIso()
    updateFormState({
      startDate,
    })
    confirmTimeslotWithPaidUpgrades(selectedOtherVenue?.urlKey ?? venue.urlKey, startDate, timeSlot.time, partySize)

    if (timeSlot.accessPersistentId && timeSlot?.timeIso) {
      const timeIso = TimeOnly.from(timeSlot.timeIso).toHoursMinutesIso()
      const otherVenueURLKey = selectedOtherVenue?.urlKey
      if (otherVenueURLKey) {
        navigateNext(timeSlot, selectedOtherVenue?.urlKey, {
          date: timeSlot.timeIso,
          timeslot_id: timeSlot.accessPersistentId,
          timeslot_time: timeIso,
          primary_venue_id: venue.id,
        })
      } else {
        navigateNext(timeSlot)
      }
    } else {
      // This should not happen. If so, there's a bug somewhere.
      // eslint-disable-next-line no-console
      console.error('A timeslot must be selected.')
    }
  }
  const selectedTimeSlotUpgrades = useMemo(
    () =>
      mapAvailabilityUpgrades(
        partySize,
        selectedTimeSlot?.selectedAutomaticUpsells,
        upgradesData?.inventories,
        upgradesData?.categories,
        selectedTimeSlot?.defaultServiceCharge,
        selectedTimeSlot?.defaultGratuity
      ),
    [
      selectedTimeSlot?.selectedAutomaticUpsells,
      selectedTimeSlot?.defaultServiceCharge,
      selectedTimeSlot?.defaultGratuity,
      upgradesData,
      partySize,
    ]
  )

  const onPrivateEventsExploreClick = useCallback(() => {
    updateFormState({ searchTab: 'group_bookings' })
  }, [updateFormState])

  const handleOnDateClick = (date: string) => {
    updateQuery({ date })
    setValue('startDate', DateOnly.from(date).toJsDate())
  }

  const onPrivateEventsMoreDetailsClick = useCallback(
    (experience: ExperienceWithAvailability) => {
      setSelectedExperience(experience)
      showModal('moreExperienceDetails')
    },
    [showModal]
  )

  const onMoreDetailsModalClose = useCallback(() => {
    hideModal()
    setSelectedExperience(undefined)
    setSelectedTimeSlot(undefined)
  }, [hideModal])

  const onPartySizeChane = useCallback(
    (value: number) => {
      updateQuery({ partySize: value?.toString() })
      updatePartySizeSearchParams(venue.urlKey, value)
      updateFormState({ partySize: value })
      trackTealiumChangeQuantity(value)
    },
    [updateFormState, updateQuery, venue.urlKey]
  )

  const onStartDateChange = useCallback(
    (value: Date) => {
      updateQuery({ date: DateOnly.fromDate(value).toIso() })
      updateDateSearchParams(venue.urlKey, DateOnly.fromDate(value).toIso())
      updateFormState({ startDate: DateOnly.fromDate(value).toIso() })
      trackTealiumChangeDate(DateOnly.fromDate(value).formatFMonthNDaySWeek())
    },
    [updateFormState, updateQuery, venue.urlKey]
  )

  const onDurationChane = useCallback(
    (value: number) => {
      updateQuery({ duration: value?.toString() })
      updateFormState({ duration: value })
    },
    [updateFormState, updateQuery]
  )

  return (
    <>
      {isModifyRoute && (
        <Flex alignItems="center" justifyContent="center" pt="xxl" pb="lm">
          <Text textStyle="h1">
            {formatMessage(reservationWidgetMessages.resWidgetModifyReservationHeader, {
              venue: venueName,
            })}
          </Text>
        </Flex>
      )}
      <StyledSearchInputGridContainer>
        <Form {...form} onSubmit={() => {}} onInvalid={() => {}}>
          <ReservationSearchComponent
            isDatesFetching={isDatesFetching}
            isDayBlocked={isDayBlocked}
            partySize={field.prop('partySize')}
            onPartySizeChange={onPartySizeChane}
            startDate={field.prop('startDate')}
            onStartDateChange={onStartDateChange}
            startTime={startTime}
            haloTimeIntervalMinutes={haloTimeIntervalMinutes}
            onTimeChange={handleFilterTimeChange}
            selectedDuration={duration}
            duration={showDurationPicker ? (field.prop('duration') as Field<number>) : undefined}
            onDurationChange={onDurationChane}
            requiredDate
          />
        </Form>
      </StyledSearchInputGridContainer>
      {isGroupWidget ? (
        <ReservationsGroupWidget
          isLoading={isFetchingVenuesInfo}
          showPrivateEvents={showPrivateEvents}
          venuesInfo={venuesInfo}
          partySize={partySize}
          startDate={startDate}
          startTime={startTime}
          haloTimeIntervalMinutes={haloTimeIntervalMinutes}
          duration={duration}
          onTimeSlotClick={(slot: AvailabilityTimeWithUpSellCost, venue: OtherVenueInfo) => onAvailabilitySelect(slot, undefined, venue)}
          onPrivateEventsMoreDetailsClick={onPrivateEventsMoreDetailsClick}
          onPrivateEventsTimeSlotClick={(
            slot: AvailabilityTimeWithUpSellCost,
            experience: ExperienceWithAvailability,
            venue?: OtherVenueInfo
          ) => onAvailabilitySelect(slot, undefined, venue, experience)}
        />
      ) : (
        <AvailabilityResults
          venuesInfo={venuesInfo}
          onClearTimeFilter={() => {
            updateQuery({ time: ReservationWidget.AllTimesOption })
            setValue('startTime', ReservationWidget.AllTimesOption)
          }}
          handleOnDateClick={handleOnDateClick}
          onAvailabilitySelect={onAvailabilitySelect}
          partySize={partySize}
          startTime={startTime}
          startDate={startDate}
          duration={duration}
          isDayBlocked={isDayBlocked}
          haloTimeIntervalMinutes={haloTimeIntervalMinutes}
          clientId={searchFormData.clientId}
          onPrivateEventsExploreClick={onPrivateEventsExploreClick}
          onPrivateEventsMoreDetailsClick={onPrivateEventsMoreDetailsClick}
          onPrivateEventsTimeSlotClick={(
            slot: AvailabilityTimeWithUpSellCost,
            experience: ExperienceWithAvailability,
            venue?: OtherVenueInfo
          ) => onAvailabilitySelect(slot, undefined, venue, experience)}
          showPrivateEvents={showPrivateEvents}
          actualId={searchFormData?.actual?.id || ''}
        />
      )}
      {activeModal === 'reservationAvailability' && selectedTimeSlot && (
        <Window active>
          <ThemeProvider theme={theme}>
            <ReservationAvailabilityModal
              timeslot={selectedTimeSlot}
              currencyCode={venue.currencyCode}
              title={
                selectedTimeSlot.publicDescriptionTitle ||
                selectedTimeSlot.publicTimeSlotDescription ||
                formatMessage(reservationWidgetMessages.resWidgetReservationDetails)
              }
              description={selectedTimeSlot.publicLongFormDescription}
              cancellationPolicy={selectedTimeSlot.cancellationPolicy}
              timeIso={selectedTimeSlot.timeIso}
              chargeType={selectedTimeSlot.chargeType}
              duration={selectedTimeSlot.duration}
              showCost={selectedTimeSlot.showCost}
              cost={(selectedTimeSlot.cost ?? 0) + selectedTimeSlot.includedUpgradesCost}
              fees={widgetSettings.isFeesInPriceDisplayed ? selectedTimeSlot.fees ?? 0 : 0}
              image={getImageFromTimeSlot(selectedTimeSlot, 'large')}
              upgrades={selectedTimeSlotUpgrades}
              onConfirm={timeslot => {
                if (selectedOtherVenue && selectedOtherVenue?.urlKey !== venue.urlKey && !isGroupWidget) {
                  showModal('confirmOtherVenueNavigation')
                } else {
                  onConfirmAvailabilitySelect(timeslot)
                }
              }}
              onClose={onSelectedTimeslotModalClose}
            />
          </ThemeProvider>
        </Window>
      )}
      {activeModal === 'confirmOtherVenueNavigation' && !!selectedOtherVenue && (
        <Window active>
          <ThemeProvider theme={theme}>
            <ConfirmOtherVenueNavigationModal
              fromVenue={venue.name}
              toVenue={selectedOtherVenue.venueName}
              onConfirm={() => {
                if (selectedTimeSlot) {
                  trackTealiumChangeVenue(selectedOtherVenue.urlKey, selectedOtherVenue.venueName, selectedOtherVenue.countryCode)
                  onConfirmAvailabilitySelect(selectedTimeSlot)
                }
              }}
              onClose={onSelectedTimeslotModalClose}
            />
          </ThemeProvider>
        </Window>
      )}
      {activeModal === 'moreExperienceDetails' && selectedExperience && (
        <Window active>
          <ExperienceDetailsModal
            experience={selectedExperience}
            currencyCode={currencyCode}
            timeSlot={selectedTimeSlot}
            onClose={onMoreDetailsModalClose}
            onConfirmSelect={onConfirmAvailabilitySelect}
          />
        </Window>
      )}
      {activeModal === 'reservationHold' && (
        <Window active>
          <ReservationHoldModal />
        </Window>
      )}
    </>
  )
}
